import "@fontsource-variable/geist-mono"

import type { FC, ReactNode } from "react"
import { useMemo } from "react"
import { Link } from "@tanstack/react-router"

import type {
	ApiReferenceEnv,
	ApiReferenceLinkProps,
} from "@productlane/ui/src/components/api-reference"
import { ApiReferenceEnvProvider } from "@productlane/ui/src/components/api-reference"

// The system mono reads wide and heavy at 12px; the portal uses Geist Mono too.
export const API_MONO_CLASS =
	"[--font-api-mono:'Geist_Mono_Variable',ui-monospace,monospace] [&_.font-mono]:font-[family-name:var(--font-api-mono)]"

// Matches the article header, so the line under the portal switcher runs across every docs pane.
export const API_PANE_HEADER_CLASS =
	"flex max-h-12 min-h-12 w-full flex-none items-center justify-end gap-2 border-b border-gray-200 py-2 pl-[17.5px] pr-3 dark:border-gray-700/80"

/** Maps a docs path tail (`slug` or `slug/group/page`) to the dashboard route. */
export function apiReferenceRoute(tabId: string, to: string): string {
	const [, groupSlug, slug] = to.split("/")
	return groupSlug && slug
		? `/edit-docs/api/${tabId}/${groupSlug}/${slug}`
		: `/edit-docs/api/${tabId}`
}

/** Shared reference views link inside the dashboard and keep its default cursor. */
export const ApiReferenceDashboardEnv: FC<{
	tabId: string
	children: ReactNode
}> = ({ tabId, children }) => {
	const env = useMemo<ApiReferenceEnv>(() => {
		const RouteLink: FC<ApiReferenceLinkProps> = ({
			to,
			className,
			children: label,
		}) => (
			<Link to={apiReferenceRoute(tabId, to)} className={className}>
				{label}
			</Link>
		)
		return {
			t: (_key, fallback) => fallback,
			Link: RouteLink,
			monoClassName: API_MONO_CLASS,
			showPointer: false,
		}
	}, [tabId])
	return (
		<ApiReferenceEnvProvider value={env}>{children}</ApiReferenceEnvProvider>
	)
}
